import { all, call, takeLatest, put, SagaReturnType } from 'redux-saga/effects';
import {
    deployEntityError,
    deployEntityRequest,
    deployEntitySuccess,
    destroyStandError,
    destroyStandRequest,
    destroyStandSuccess,
    getBranchesForDeployError,
    getBranchesForDeployRequest,
    getBranchesForDeploySuccess,
    getBuildDetailsError,
    getBuildDetailsRequest,
    getBuildDetailsSuccess,
    getBuildsForDeployError,
    getBuildsForDeployRequest,
    getBuildsForDeploySuccess,
    getDeployedSchemesForEntityError,
    getDeployedSchemesForEntityRequest,
    getDeployedSchemesForEntitySuccess,
    getStandsBranchesFilterError,
    getStandsBranchesFilterRequest,
    getStandsBranchesFilterSuccess,
    getStandsBuildsFilterError,
    getStandsBuildsFilterRequest,
    getStandsBuildsFilterSuccess,
    getStandsError,
    getStandsRequest,
    getStandsSuccess,
    processWebSocketMessageAction,
    removeStand,
    updateStandError,
    updateStandInfo,
    updateStandRequest,
    updateStandSuccess,
    updateVMByStandIddSuccess,
    updateVMByStandIdError,
    updateVMByStandIdRequest,
} from './index';
import {
    destroyStand,
    getBranchesForDeploy,
    getBuildDetails,
    getBuildsForDeploy,
    getDeployedSchemesForEntity,
    getStands,
    getStandsBranches,
    getStandsBuilds,
    startEntityDeploy,
    updateStand,
    updateVMByStandId,
} from '@pages/stands/api/standsApi';
import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import {
    DeployEntityRequestParams,
    GetBranchesParams,
    GetBuildDetailsParams,
    Stand,
    StandEntityType,
    StandWebSocketMessage,
    UpdateStandRequestParams,
    UpdateVMByStandIdRequestParams,
} from '../types';
import { AxiosError } from 'axios';

export type GetDeployedSchemesForEntityParams = { entity: StandEntityType; id: number };

type DeployEntityType = SagaReturnType<typeof startEntityDeploy>;

export function* deployEntitySaga(action: PayloadAction<DeployEntityRequestParams>): SagaIterator {
    const { type, id, params } = action.payload;

    try {
        const response: DeployEntityType = yield call(startEntityDeploy, { type, id, params });
        yield put(deployEntitySuccess(response.data));
    } catch (error: unknown) {
        if (error instanceof Error) {
            yield put(deployEntityError(error as AxiosError));
        }
    }
}

export function* destroyStandSaga(action: PayloadAction<Stand['pk']>): SagaIterator {
    const standId = action.payload;

    try {
        yield call(destroyStand, standId);
        yield put(destroyStandSuccess());
    } catch (error: unknown) {
        if (error instanceof Error) {
            yield put(destroyStandError(error as AxiosError));
        }
    }
}

export function* updateStandSaga(action: PayloadAction<UpdateStandRequestParams>): SagaIterator {
    const { pk, data } = action.payload;

    try {
        yield call(updateStand, pk, data);
        yield put(updateStandInfo({ pk, ...data }));
        yield put(updateStandSuccess());
    } catch (error: unknown) {
        if (error instanceof Error) {
            yield put(updateStandError(error as AxiosError));
        }
    }
}

type GetStandsReturnType = SagaReturnType<typeof getStands>;
export function* getStandsSaga(action: any): SagaIterator {
    const { ...params } = action.payload;

    try {
        const response: GetStandsReturnType = yield call(getStands, params);
        yield put(getStandsSuccess({ items: response.data.items ?? [], count: response.data.count ?? 0 }));
    } catch (error: unknown) {
        if (error instanceof Error) {
            yield put(getStandsError(error as AxiosError));
        }
    }
}

const MAP_ACTION_TO_FN = {
    destroy: removeStand,
    update: updateStandInfo,
    info: updateStandInfo,
} as const;

export function* processWebSocketMessageSaga(action: PayloadAction<StandWebSocketMessage>) {
    const { action: messageAction, ...messageData } = action.payload;
    const fn = MAP_ACTION_TO_FN[messageAction];

    if (fn) {
        yield put(fn(messageData));
    }
}

type GetDeployedSchemesForEntityResponse = SagaReturnType<typeof getDeployedSchemesForEntity>;
export function* getDeployedSchemesForEntitySaga(action: PayloadAction<GetDeployedSchemesForEntityParams>) {
    const { entity, id } = action.payload;

    try {
        const response: GetDeployedSchemesForEntityResponse = yield call(getDeployedSchemesForEntity, entity, id);
        yield put(getDeployedSchemesForEntitySuccess(response.data));
    } catch (error: unknown) {
        if (error instanceof Error) {
            yield put(getDeployedSchemesForEntityError(error as AxiosError));
        }
    }
}

type GetBranchesResponse = SagaReturnType<typeof getBranchesForDeploy>;
export function* getBranchesForDeploySaga(action: PayloadAction<GetBranchesParams>) {
    try {
        const response: GetBranchesResponse = yield call(getBranchesForDeploy, action.payload);
        yield put(getBranchesForDeploySuccess(response.data));
    } catch (error) {
        yield put(getBranchesForDeployError(error));
    }
}

type GetBuildsResponse = SagaReturnType<typeof getBuildsForDeploy>;
export function* getBuildsForDeploySaga(action: PayloadAction<string>) {
    try {
        const response: GetBuildsResponse = yield call(getBuildsForDeploy, action.payload);
        yield put(getBuildsForDeploySuccess(response));
    } catch (error) {
        yield put(getBuildsForDeployError(error));
    }
}

type GetBuildDetailsResponse = SagaReturnType<typeof getBuildDetails>;
export function* getBuildDetailsSaga(action: PayloadAction<GetBuildDetailsParams>) {
    const { branch, build } = action.payload;

    try {
        const response: GetBuildDetailsResponse = yield call(getBuildDetails, branch, build);
        yield put(getBuildDetailsSuccess(response));
    } catch (error) {
        yield put(getBuildDetailsError(error));
    }
}

export function* updateVMByStandIdSaga(action: PayloadAction<UpdateVMByStandIdRequestParams>): SagaIterator {
    try {
        yield call(updateVMByStandId, action.payload);
        yield put(updateVMByStandIddSuccess());
    } catch (error: unknown) {
        if (error instanceof Error) {
            yield put(updateVMByStandIdError(error as AxiosError));
        }
    }
}

export interface GetStandsBuildsParams {
    branch?: string;
}

type GetStandsBuildsResponse = SagaReturnType<typeof getStandsBuilds>;
export function* getStandsBuildsFilterSaga(action: PayloadAction<GetStandsBuildsParams>) {
    try {
        const response: GetStandsBuildsResponse = yield call(getStandsBuilds, action.payload.branch);
        yield put(getStandsBuildsFilterSuccess(response));
    } catch (err) {
        yield put(getStandsBuildsFilterError(err));
    }
}

type GetStandsBranchesResponse = SagaReturnType<typeof getStandsBranches>;
export function* getStandsBranchesFilterSaga() {
    try {
        const response: GetStandsBranchesResponse = yield call(getStandsBranches);
        yield put(getStandsBranchesFilterSuccess(response));
    } catch (err) {
        yield put(getStandsBranchesFilterError(err));
    }
}

export default function* standsWatcherSaga() {
    yield all([
        takeLatest(deployEntityRequest.type, deployEntitySaga),
        takeLatest(destroyStandRequest.type, destroyStandSaga),
        takeLatest(updateStandRequest.type, updateStandSaga),
        takeLatest(getStandsRequest.type, getStandsSaga),
        takeLatest(processWebSocketMessageAction.type, processWebSocketMessageSaga),
        takeLatest(getDeployedSchemesForEntityRequest.type, getDeployedSchemesForEntitySaga),
        takeLatest(getBranchesForDeployRequest.type, getBranchesForDeploySaga),
        takeLatest(getBuildsForDeployRequest.type, getBuildsForDeploySaga),
        takeLatest(getBuildDetailsRequest.type, getBuildDetailsSaga),
        takeLatest(updateVMByStandIdRequest.type, updateVMByStandIdSaga),
        takeLatest(getStandsBranchesFilterRequest.type, getStandsBranchesFilterSaga),
        takeLatest(getStandsBuildsFilterRequest.type, getStandsBuildsFilterSaga),
    ]);
}
